import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertTriangle, Check, Info } from "lucide-react";
import { cn } from "./cn.ts";
import { id as makeId } from "@suite/core";

type ToastTone = "info" | "success" | "warning";

interface ToastItem {
  id: string;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  toast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const icons = {
  info: Info,
  success: Check,
  warning: AlertTriangle,
} as const;

const tones: Record<ToastTone, string> = {
  info: "border-border",
  success: "border-ok",
  warning: "border-warn",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, tone: ToastTone = "info") => {
    const item = { id: makeId("t"), message, tone };
    setItems((prev) => [...prev, item]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== item.id));
    }, 3200);
  }, []);

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* aria-live so the message reaches screen readers, not just the screen. */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4"
      >
        {items.map((t) => {
          const Icon = icons[t.tone];
          return (
            <div
              key={t.id}
              className={cn(
                "pointer-events-auto flex max-w-sm items-center gap-2.5 rounded-lg border bg-surface",
                "px-3.5 py-2.5 text-sm shadow-lg",
                tones[t.tone],
              )}
            >
              <Icon className="size-4 shrink-0 text-fg-muted" />
              <span>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  // A no-op fallback keeps components usable outside a provider (e.g. in tests)
  // instead of throwing during render.
  return ctx ?? { toast: () => undefined };
}
